import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { UserPlus, Search, Trash2, Pencil, X, Check } from "lucide-react";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { useLang } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const SEGMENTS = ["Nouveau", "Régulier", "Fidèle", "Occasionnel", "À réactiver", "Inactif"];
const SEG_LABELS = {
  fr: { "Nouveau": "Nouveau", "Régulier": "Régulier", "Fidèle": "Fidèle", "Occasionnel": "Occasionnel", "À réactiver": "À réactiver", "Inactif": "Inactif" },
  en: { "Nouveau": "New", "Régulier": "Regular", "Fidèle": "Loyal", "Occasionnel": "Occasional", "À réactiver": "To reactivate", "Inactif": "Inactive" },
  es: { "Nouveau": "Nuevo", "Régulier": "Habitual", "Fidèle": "Fiel", "Occasionnel": "Ocasional", "À réactiver": "Por reactivar", "Inactif": "Inactivo" },
};
const ALL_LABEL = { fr: "Tous les segments", en: "All segments", es: "Todos los segmentos" };
const TXT = {
  first: { fr: "Prénom", en: "First name", es: "Nombre" },
  last: { fr: "Nom", en: "Name", es: "Apellido" },
  phone: { fr: "Téléphone", en: "Phone", es: "Teléfono" },
  search: { fr: "Rechercher…", en: "Search…", es: "Buscar…" },
  yes: { fr: "Oui", en: "Yes", es: "Sí" },
  no: { fr: "Non", en: "No", es: "No" },
  edit: { fr: "Modifier", en: "Edit", es: "Editar" },
  add: { fr: "Ajouter", en: "Add", es: "Añadir" },
};
const empty = { first_name: "", last_name: "", phone: "", email: "", segment: "Nouveau", last_visit: "", notes: "", consent: false };

export function ClientCRM() {
  const { user, demo } = useApp();
  const { lang } = useLang();
  const L = (m) => m[lang] || m.fr;
  const segLabel = (s) => (SEG_LABELS[lang] || SEG_LABELS.fr)[s] || s;
  const isAccount = user && !demo;
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [seg, setSeg] = useState("all");
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  useEffect(() => { if (isAccount) api.get("/clients").then(({ data }) => setClients(data.items || [])).catch((e) => console.error("clients load", e)); }, [isAccount]);

  const reset = () => { setForm(empty); setEditing(null); };
  const save = async () => {
    if (!form.first_name && !form.last_name) { toast.error("Renseignez au moins un nom."); return; }
    if (isAccount) {
      try {
        if (editing) { await api.put(`/clients/${editing}`, form); setClients((c) => c.map((x) => x.id === editing ? { ...x, ...form } : x)); }
        else { const { data } = await api.post("/clients", form); setClients((c) => [data, ...c]); }
        toast.success("Client enregistré !");
      } catch (e) { console.error(e); toast.error("Échec de l'enregistrement."); }
    } else {
      if (editing) setClients((c) => c.map((x) => x.id === editing ? { ...x, ...form } : x));
      else setClients((c) => [{ id: `local_${Date.now()}`, ...form }, ...c]);
      toast.success("Client enregistré !");
    }
    reset();
  };
  const edit = (c) => { setEditing(c.id); setForm({ ...empty, ...c }); };
  const del = async (id) => {
    if (isAccount && !id.startsWith("local_")) { try { await api.delete(`/clients/${id}`); } catch (e) { console.error(e); } }
    setClients((c) => c.filter((x) => x.id !== id)); setConfirmDel(null); toast.success("Client supprimé.");
  };

  const filtered = clients.filter((c) => {
    const s = `${c.first_name} ${c.last_name} ${c.email} ${c.phone}`.toLowerCase();
    return (seg === "all" || c.segment === seg) && s.includes(q.toLowerCase());
  });

  return (
    <div className="lb-card p-5 mt-4" data-testid="client-crm">
      <h3 className="font-display font-bold text-lg mb-1">Mes clients (mini-CRM)</h3>
      <p className="text-xs text-[hsl(var(--muted-foreground))] mb-4">{isAccount ? "Ajoutez, recherchez et gérez votre base clients." : "Ajoutez, recherchez et gérez votre base clients (session démo)."}</p>

      {/* Add / edit form */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
        <Input data-testid="crm-first" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} placeholder={L(TXT.first)} className="bg-white/5 border-white/10 h-10" />
        <Input data-testid="crm-last" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} placeholder={L(TXT.last)} className="bg-white/5 border-white/10 h-10" />
        <Input data-testid="crm-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder={L(TXT.phone)} className="bg-white/5 border-white/10 h-10" />
        <Input data-testid="crm-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="bg-white/5 border-white/10 h-10" />
        <Select value={form.segment} onValueChange={(v) => setForm({ ...form, segment: v })}>
          <SelectTrigger data-testid="crm-segment" className="bg-white/5 border-white/10 h-10"><SelectValue /></SelectTrigger>
          <SelectContent>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{segLabel(s)}</SelectItem>)}</SelectContent>
        </Select>
        <div className="flex gap-2">
          <Button data-testid="crm-save" onClick={save} className="flex-1 rounded-full h-10 bg-gradient-to-r from-primary to-neon text-white lb-glow-btn text-xs">{editing ? <><Check size={14} className="mr-1" /> {L(TXT.edit)}</> : <><UserPlus size={14} className="mr-1" /> {L(TXT.add)}</>}</Button>
          {editing && <Button data-testid="crm-cancel" onClick={reset} variant="outline" className="rounded-full h-10 border-white/10 bg-white/5 px-3"><X size={14} /></Button>}
        </div>
      </div>

      {/* Search + filter */}
      <div className="flex gap-2 mb-3">
        <div className="relative flex-1"><Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[hsl(var(--muted-foreground))]" /><Input data-testid="crm-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={L(TXT.search)} className="bg-white/5 border-white/10 h-10 pl-9" /></div>
        <Select value={seg} onValueChange={setSeg}>
          <SelectTrigger data-testid="crm-filter" className="bg-white/5 border-white/10 h-10 w-40"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">{L(ALL_LABEL)}</SelectItem>{SEGMENTS.map((s) => <SelectItem key={s} value={s}>{segLabel(s)}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {/* List */}
      <div className="space-y-2 max-h-80 overflow-y-auto" data-testid="crm-list">
        {filtered.length === 0 ? <p className="text-xs text-[hsl(var(--muted-foreground))] text-center py-4">Aucun client. Ajoutez votre premier client ci-dessus.</p>
          : filtered.map((c) => (
            <div key={c.id} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5" data-testid={`crm-row-${c.id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{c.first_name} {c.last_name || ""}</p>
                <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{[c.phone, c.email].filter(Boolean).join(" · ") || "—"}</p>
              </div>
              <span className="text-[10px] px-2 py-0.5 rounded-full bg-primary/15 text-neon shrink-0">{segLabel(c.segment)}</span>
              <button data-testid={`crm-edit-${c.id}`} onClick={() => edit(c)} className="inline-flex items-center justify-center min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 text-[hsl(var(--muted-foreground))] hover:text-white shrink-0"><Pencil size={14} /></button>
              {confirmDel === c.id ? (
                <span className="flex gap-1 shrink-0">
                  <button data-testid={`crm-confirm-del-${c.id}`} onClick={() => del(c.id)} className="text-red-400 text-xs font-semibold">{L(TXT.yes)}</button>
                  <button onClick={() => setConfirmDel(null)} className="text-[hsl(var(--muted-foreground))] text-xs">{L(TXT.no)}</button>
                </span>
              ) : (
                <button data-testid={`crm-del-${c.id}`} onClick={() => setConfirmDel(c.id)} className="inline-flex items-center justify-center min-w-[40px] min-h-[40px] sm:min-w-0 sm:min-h-0 text-[hsl(var(--muted-foreground))] hover:text-red-400 shrink-0"><Trash2 size={14} /></button>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}
